import api from './axiosConfig';

export type ShareKind = 'note' | 'document' | 'storyboard';
export type ShareRole = 'viewer' | 'editor';

// What the server says about the requester's hold on a note, document or
// storyboard. Owners get only their role; everyone else also learns whose it
// is and who last changed it.
export type Access =
  | { role: 'owner' }
  | { role: ShareRole; owner: { id: number; name: string }; last_edited_by: string | null };

export interface Share {
  id: number;
  kind: ShareKind;
  target_id: number;
  email: string;
  role: ShareRole;
  user: { id: number; name: string } | null;
  // The invitee's address isn't verified yet, so they have to open the link
  pending: boolean;
  created_at: string;
  invite_url?: string;
  emailed?: boolean;
}

export interface SharedItem {
  share_id: number;
  kind: ShareKind;
  id: number;
  name: string;
  owner: { id: number; name: string };
  role: ShareRole;
  updated_at: string;
}

export interface InvitePreview {
  kind: ShareKind;
  name: string;
  invited_by: string;
  role: ShareRole;
  email: string;
  claimed: boolean;
}

/** Missing access (older payloads, or a create response) means it's yours. */
export const isOwner = (access?: Access | null): boolean => !access || access.role === 'owner';
export const canEdit = (access?: Access | null): boolean => !access || access.role !== 'viewer';

export const listShares = async (kind: ShareKind, id: number): Promise<Share[]> => {
  const response = await api.get('/shares/', { params: { kind, id } });
  return response.data.shares;
};

export const createShare = async (
  kind: ShareKind, id: number, email: string, role: ShareRole,
): Promise<Share> => {
  // The dialog answers a 402 with the collaboration paywall itself, so the
  // interceptor's generic "view plans?" prompt stays out of the way.
  const response = await api.post('/shares/', { kind, id, email, role }, { handlesUpgrade: true });
  return response.data;
};

export const updateShareRole = async (shareId: number, role: ShareRole): Promise<Share> => {
  const response = await api.patch(`/shares/${shareId}/`, { role });
  return response.data;
};

export const deleteShare = async (shareId: number): Promise<void> => {
  await api.delete(`/shares/${shareId}/`);
};

export const listSharedWithMe = async (): Promise<SharedItem[]> => {
  const response = await api.get('/shared-with-me/');
  return response.data.items;
};

export const getInvite = async (token: string): Promise<InvitePreview> => {
  const response = await api.get(`/invites/${token}/`);
  return response.data;
};

export const acceptInvite = async (token: string): Promise<{ kind: ShareKind; id: number }> => {
  const response = await api.post(`/invites/${token}/accept/`);
  return response.data;
};

/** Where in the app a shared item opens. Full URLs, so Home boots into it. */
export const pathForItem = (kind: ShareKind, id: number): string => {
  if (kind === 'document') return `/?view=documents&docId=${id}`;
  if (kind === 'storyboard') return `/?view=storyboards&boardId=${id}`;
  return `/?view=shared&noteId=${id}`;
};

export interface Conflict {
  updated_at: string;
  content: string;
  last_edited_by: string;
}

/** The 409 a save gets when someone else saved since our base_updated_at. */
export const conflictFrom = (error: any): Conflict | null => {
  const data = error?.response?.data;
  if (error?.response?.status !== 409 || data?.error !== 'conflict') return null;
  return { updated_at: data.updated_at, content: data.content ?? '', last_edited_by: data.last_edited_by || '' };
};
